import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { updateUserProfile } from "../api/api";
import {
  getProfileCached,
  updateProfileCache,
  uploadProfileImage
} from "../api/profileApi";

import LeftSidebar from "../components/sidebar";
import Header from "../components/Header";
import ProfileMenu from "../components/ProfileMenu";

export default function Profile() {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    address: "",
    profile_image_url: ""
  });
  const [originalProfile, setOriginalProfile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await getProfileCached();
      setProfile({
        first_name: res.data.first_name || "",
        last_name: res.data.last_name || "",
        email: res.data.email || "",
        mobile: res.data.mobile || "",
        address: res.data.address || "",
        profile_image_url: res.data.profile_image_url || ""
      });
      setOriginalProfile({
        first_name: res.data.first_name || "",
        last_name: res.data.last_name || "",
        email: res.data.email || "",
        mobile: res.data.mobile || "",
        address: res.data.address || "",
        profile_image_url: res.data.profile_image_url || ""
      });
    } catch {
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const digitsOnly = (value || "").replace(/\D/g, "").slice(0, 10);
      setProfile((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    if (originalProfile) {
      setProfile({
        first_name: originalProfile.first_name || "",
        last_name: originalProfile.last_name || "",
        email: originalProfile.email || "",
        mobile: originalProfile.mobile || "",
        address: originalProfile.address || "",
        profile_image_url: originalProfile.profile_image_url || ""
      });
    }
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    const next = {
      first_name: (profile.first_name || "").trim(),
      last_name: (profile.last_name || "").trim(),
      mobile: (profile.mobile || "").trim(),
      address: (profile.address || "").trim()
    };
    const prev = {
      first_name: (originalProfile?.first_name || "").trim(),
      last_name: (originalProfile?.last_name || "").trim(),
      mobile: (originalProfile?.mobile || "").trim(),
      address: (originalProfile?.address || "").trim()
    };

    const payload = {};
    Object.keys(next).forEach((key) => {
      if (next[key] !== prev[key]) {
        payload[key] = next[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to update");
      setUpdating(false);
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(payload, "mobile") &&
      payload.mobile !== "" &&
      !/^\d{10}$/.test(payload.mobile)
    ) {
      toast.error("Phone format: 10 digits only (example: 9876543210)");
      setUpdating(false);
      return;
    }

    try {
      await updateUserProfile(payload);
      const mergedProfile = {
        ...profile,
        ...payload
      };
      setProfile(mergedProfile);
      setOriginalProfile((prevOriginal) => ({
        ...(prevOriginal || {}),
        ...payload
      }));
      updateProfileCache(mergedProfile);
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUpdating(true);
      const res = await uploadProfileImage(file);
      const imageUrl = res?.data?.profile_image_url || "";

      setProfile((prev) => ({
        ...prev,
        profile_image_url: imageUrl
      }));
      setOriginalProfile((prev) => ({
        ...(prev || {}),
        profile_image_url: imageUrl
      }));

      updateProfileCache({ profile_image_url: imageUrl });
      toast.success("Profile image updated successfully!");
    } catch {
      toast.error("Failed to upload profile image");
    } finally {
      setUpdating(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" />
        <p className="mt-2">Loading profile...</p>
      </div>
    );
  }

  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-lg-3 left-sidebar-main">
            <LeftSidebar />
          </div>

          <div className="col-lg-9">
            <Header />

            <div className="row contact-section">
              <div className="col-sm-4 profile-sidebar-menu">
                 <div className="sec1">
                  <ProfileMenu />
                </div>
              </div>

              <div className="col-lg-8">
                <div className="sec2">
                  <div className="form-section">
                    <div className="row mb-3">
                      <div className="col-sm-8">
                        {isEditing ? (
                          <>
                            <h3>About Me</h3>
                            <p>Fill out your basic information</p>
                          </>
                        ) : (
                          <>
                            <h3>Profile Review</h3>
                            <p>Review all your provided info</p>
                          </>
                        )}
                      </div>

                      <div className="col-sm-4 profile-img">
                        <div className="profileImageWrapper">
                          <img
                            src={profile.profile_image_url || "/images/profile.png"}
                            className="profileImage"
                            alt="Profile"
                          />

                          {/* {isEditing && (
                            <label className="editImageBtn">
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageChange}
                              />
                              <i className="bi bi-pencil-fill"></i>
                            </label>
                          )} */}
                        </div>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className="row g-4">

                        {/* First Name */}
                        <div className="col-md-6">
                          <label className="form-label">First Name</label>
                          {isEditing ? (
                             <div className="iconInput">
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="first_name"
                                    value={profile.first_name}
                                    onChange={handleInputChange}
                                    placeholder="Enter first name"
                                  />
                                  <img src="/icons/user2.svg" alt="user"/>
                            </div>
                          ) : (
                            <div className="viewField">{profile.first_name || "-"}</div>
                          )}
                        </div>

                        {/* Last Name */}
                        <div className="col-md-6">
                          <label className="form-label">Last Name</label>
                          {isEditing ? (
                            <div className="iconInput">
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="last_name"
                                    value={profile.last_name}
                                    onChange={handleInputChange}
                                    placeholder="Enter last name"
                                  />
                                  <img src="/icons/user2.svg" alt="user"/>
                                </div>
                          ) : (
                            <div className="viewField">{profile.last_name || "-"}</div>
                          )}
                        </div>

                        {/* Phone */}
                        <div className="col-md-6">
                          <label className="form-label">Phone</label>
                          {isEditing ? (
                            <div className="iconInput">
                                  <input
                                    type="tel"
                                    className="form-control"
                                    name="mobile"
                                    value={profile.mobile}
                                    onChange={handleInputChange}
                                    placeholder="Enter 10-digit mobile number"
                                    maxLength="10"
                                    inputMode="numeric"
                                  />
                              
                                  <img src="/icons/phone3.svg" alt="phone"/>
                                </div>
                          ) : (
                            <div className="viewField">{profile.mobile || "-"}</div>
                          )}
                        </div>

                        {/* Email */}
                        <div className="col-md-6">
                          <label className="form-label">Email</label>
                          {isEditing ? (
                                <div className="iconInput">
                                  <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={profile.email}
                                   // onChange={handleInputChange}
                                    placeholder="Enter email address"
                                    disabled
                                  />
                                  <img src="/icons/email.svg" alt="email"/>
                                </div>
                          ) : (
                            <div className="viewField">{profile.email || "-"}</div>
                          )}
                        </div>

                        {/* Address */}
                        <div className="col-md-6">
                          <label className="form-label">Address</label>
                          {isEditing ? (
                            <div className="iconInput">
                                  <input
                                    className="form-control"
                                    name="address"
                                    rows="3"
                                    value={profile.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter your address"
                                  ></input>
                                  <img src="/icons/home.svg" alt="address"/>
                            </div>
                          ) : (
                            <div className="viewField">{profile.address || "-"}</div>
                          )}
                        </div>

                      </div>

                      <div className="cta-section">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              className="btn4"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>

                            <button
                              type="submit"
                              className="btn5"
                              disabled={updating}
                            >
                              {updating ? "Updating..." : "Save Changes"}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn5"
                            onClick={() => setIsEditing(true)}
                          >
                            Edit Info
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
